import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ExclamationTriangleIcon, CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import { CurrencyDollarIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import api from '../../utils/api';

const C = {
  navy: '#0D1B2A', teal: '#00B4D8', success: '#10B981', warning: '#F59E0B',
  error: '#EF4444', dark: '#1F2937', grey: '#6B7280', border: '#E5E7EB', bg: '#F0F7FA',
};

function StatBox({ label, value, sub, color }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.border}`, padding: '20px 24px' }}>
      <div style={{ fontSize: 13, color: C.grey, fontWeight: 600, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: color || C.dark }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: C.grey, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function SuperAdminAPIUsage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/superadmin/api-costs')
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const month = data?.month || 0;
  const today = data?.today || 0;
  const projected = data?.projected || 0;
  const partners = data?.partners || [];
  const daily = data?.daily || [];
  const alerts = data?.alerts || [];
  const breakdown = data?.breakdown || {};
  const blockedToday = data?.blocked_today || 0;
  const hasRealData = month > 0;

  // Format daily data for chart — fill missing days with 0
  const chartDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const chartData = daily.length > 0
    ? daily.map(d => ({ day: new Date(d.day + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short' }), cost: d.cost }))
    : chartDays.map(day => ({ day, cost: 0 }));

  return (
    <Layout role="superadmin">
      <div style={{ padding: 'clamp(16px,4vw,32px)', maxWidth: 1200 }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: C.navy, margin: 0 }}>API Usage & Costs</h1>
          <p style={{ color: C.grey, fontSize: 13, marginTop: 4 }}>Real Claude API usage across all partners — your actual costs.</p>
        </div>

        {/* ── Stat cards ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
          <StatBox label="Today's Cost" value={`$${today.toFixed(4)}`} color={today > 5 ? C.warning : C.success} sub="Real Claude API spend" />
          <StatBox label="This Month" value={`$${month.toFixed(2)}`} color={month > 50 ? C.error : month > 20 ? C.warning : C.success} sub={`Projected: $${projected.toFixed(2)}`} />
          <StatBox label="Active Partners" value={partners.filter(p => p.cost > 0).length} color={C.teal} sub="with API usage this month" />
          <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.border}`, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
            {blockedToday > 0
              ? <ExclamationTriangleIcon style={{ width: 32, height: 32, color: C.warning, flexShrink: 0 }} />
              : <CheckCircleIcon style={{ width: 32, height: 32, color: C.success, flexShrink: 0 }} />}
            <div>
              <div style={{ fontWeight: 700, color: blockedToday > 0 ? C.warning : C.success, fontSize: 15 }}>Circuit Breaker</div>
              <div style={{ fontSize: 12, color: C.grey }}>{blockedToday > 0 ? `${blockedToday} blocked today` : 'All systems normal'}</div>
            </div>
          </div>
        </div>

        {/* ── No data notice ── */}
        {!loading && !hasRealData && (
          <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 12, padding: '16px 20px', marginBottom: 24, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <InformationCircleIcon style={{ width: 20, height: 20, color: '#3B82F6', flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 700, color: '#1E40AF', marginBottom: 4 }}>No API usage recorded this month yet</div>
              <div style={{ fontSize: 13, color: '#3730A3' }}>
                API costs are logged when students use AI lessons. Once active students start using the AI tutor, their usage will appear here broken down by partner.
                {Object.keys(breakdown).length > 0 && ` Internal batch calls: ${Object.entries(breakdown).map(([k,v]) => `${k}: $${v.toFixed(4)}`).join(', ')}.`}
              </div>
            </div>
          </div>
        )}

        {/* ── Student alerts ── */}
        {alerts.length > 0 && (
          <div style={{ background: '#FEF9C3', border: `1px solid ${C.warning}`, borderRadius: 12, padding: 16, marginBottom: 24, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <ExclamationTriangleIcon style={{ width: 20, height: 20, color: C.warning, flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 700, color: '#92400E', marginBottom: 8 }}>Students approaching API cost limit</div>
              {alerts.map((a, i) => (
                <div key={i} style={{ fontSize: 13, color: '#78350F', marginBottom: 6 }}>
                  <span style={{ fontWeight: 600 }}>{a.student}</span> ({a.tenant}) — ${a.cost.toFixed(2)} / ${a.limit}.00
                  <div style={{ height: 4, background: '#E5E7EB', borderRadius: 2, marginTop: 4, maxWidth: 200 }}>
                    <div style={{ height: 4, background: C.warning, borderRadius: 2, width: `${Math.min(100, (a.cost / a.limit) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Charts ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
          <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${C.border}`, padding: 24 }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 700, color: C.dark }}>Daily Cost Trend (7 days)</h3>
            {chartData.every(d => d.cost === 0) ? (
              <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.grey, fontSize: 13 }}>
                No API calls in the last 7 days
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${v}`} />
                  <Tooltip formatter={v => [`$${Number(v).toFixed(4)}`, 'Cost']} />
                  <Line dataKey="cost" stroke={C.teal} strokeWidth={3} dot={{ fill: C.teal, r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${C.border}`, padding: 24 }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 700, color: C.dark }}>Cost by Partner</h3>
            {partners.length === 0 ? (
              <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.grey, fontSize: 13 }}>
                No partner usage this month
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={partners.map(p => ({ name: p.name?.split(' ')[0] || `Partner ${p.id}`, cost: p.cost }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${v}`} />
                  <Tooltip formatter={v => [`$${Number(v).toFixed(4)}`, 'Cost']} />
                  <Bar dataKey="cost" fill={C.teal} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* ── Partner table ── */}
        <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: 24 }}>
          <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.dark }}>Cost by Partner — Detailed</h3>
            <span style={{ fontSize: 12, color: C.grey }}>This month's real API usage</span>
          </div>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: C.grey }}>Loading...</div>
          ) : partners.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: C.grey }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>No partner API usage this month</div>
              <div style={{ fontSize: 13 }}>Usage will appear here once students start using AI lessons.</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F9FAFB' }}>
                  {['Partner', 'API Calls', 'Cost This Month', 'Avg/Call'].map(h => (
                    <th key={h} style={{ padding: '12px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: C.grey, textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {partners.map((p, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={{ padding: '14px 20px', fontWeight: 600, color: C.dark }}>{p.name || `Partner ${p.id}`}</td>
                    <td style={{ padding: '14px 20px', color: C.grey }}>{p.calls.toLocaleString()}</td>
                    <td style={{ padding: '14px 20px', fontWeight: 700, color: p.cost > 20 ? C.warning : C.dark }}>${p.cost.toFixed(4)}</td>
                    <td style={{ padding: '14px 20px', color: C.grey }}>${p.calls > 0 ? (p.cost / p.calls).toFixed(6) : '0.000000'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Call type breakdown ── */}
        {Object.keys(breakdown).length > 0 && (
          <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${C.border}`, padding: 24 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: C.dark }}>By Call Type</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {Object.entries(breakdown).map(([type, cost]) => (
                <div key={type} style={{ padding: '10px 16px', borderRadius: 10, background: C.bg, border: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 12, color: C.grey, marginBottom: 4 }}>{type.replace(/_/g, ' ')}</div>
                  <div style={{ fontWeight: 700, color: C.navy }}>${Number(cost).toFixed(4)}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 16, fontSize: 12, color: C.grey, fontStyle: 'italic' }}>
              Note: Internal batch calls (lesson generation, weekly summaries) are included in these totals. These are not charged to partners.
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
